import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { getBankAccounts } from '../../services/bankAccountService';
import { createWithdraw } from '../../services/paymentService';

const WithdrawNew = ({ auth }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [bankAccounts, setBankAccounts] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [formData, setFormData] = useState({
        amount: '',
        bankAccountId: '',
        medium: 'NEFT'
    });
    const [withdrawResult, setWithdrawResult] = useState(null);

    const WITHDRAWAL_MEDIUMS = [
        { value: 'NEFT', label: 'NEFT' },
        { value: 'RTGS', label: 'RTGS' },
        { value: 'IMPS', label: 'IMPS' },
        { value: 'crypto', label: 'Crypto' }
    ];

    useEffect(() => {
        if (!auth.isAuthenticated || !auth.token) {
            navigate(process.env.PUBLIC_URL + '/sign-in', { replace: true });
            return;
        }
        
        fetchBankAccounts();
    }, [auth, navigate]);

    const fetchBankAccounts = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getBankAccounts();
            
            if (response.success) {
                const accounts = response.data || [];
                setBankAccounts(Array.isArray(accounts) ? accounts : []);
                
                // Select primary account by default
                const primaryAccount = accounts.find(acc => acc.isPrimary && acc.status === 'active');
                const firstActiveAccount = accounts.find(acc => acc.status === 'active');
                const defaultAccount = primaryAccount || firstActiveAccount;

                if (defaultAccount) {
                    setFormData(prev => ({
                        ...prev,
                        bankAccountId: defaultAccount._id
                    }));
                    setSelectedAccount(defaultAccount);
                }
            } else {
                setBankAccounts([]);
            }
        } catch (error) {
            console.error('Error fetching bank accounts:', error);
            setError(error.message || 'Failed to fetch bank accounts');
            setBankAccounts([]);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (name === 'bankAccountId') {
            const account = bankAccounts.find(acc => acc._id === value);
            setSelectedAccount(account);
        }
        setError(null);
        setSuccess(null);
    };

    const validateForm = () => {
        if (!formData.amount || isNaN(formData.amount) || formData.amount <= 0) {
            setError('Please enter a valid amount');
            return false;
        }

        if (!formData.bankAccountId) {
            setError('Please select a bank account');
            return false;
        }

        if (!formData.medium) {
            setError('Please select a withdrawal medium');
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            setSubmitting(true);
            setError(null);
            setSuccess(null);

            const response = await createWithdraw(formData);
            console.log('Withdrawal response:', response);

            if (response.success) {
                const withdrawalData = response.data;
                
                setWithdrawResult({
                    id: withdrawalData.withdrawalId,
                    reference: withdrawalData.reference,
                    amount: Number(withdrawalData.amount),
                    status: withdrawalData.status,
                    medium: withdrawalData.medium
                });

                setSuccess(response.message || 'Withdrawal submitted successfully!');

                // Reset form
                setFormData({
                    amount: '',
                    bankAccountId: selectedAccount?._id || '',
                    medium: 'NEFT'
                });

                // Clear success message after 10 seconds
                setTimeout(() => {
                    setSuccess(null);
                    setWithdrawResult(null);
                }, 10000);

            } else {
                // Handle verification status error
                if (response.verificationStatus) {
                    let errorMessage = response.message || 'Verification required for withdrawals';
                    
                    const { emailVerified, mobileVerified } = response.verificationStatus;
                    const verificationDetails = [];

                    if (!emailVerified) {
                        verificationDetails.push('Email verification pending');
                    }
                    if (!mobileVerified) {
                        verificationDetails.push('Mobile verification pending');
                    }

                    if (!verificationDetails.length) {
                        verificationDetails.push('KYC verification pending');
                    }

                    errorMessage += '\n\nPending Verifications:\n' + verificationDetails.map(detail => `• ${detail}`).join('\n');
                    setError(errorMessage);
                } else {
                    setError(response.message || 'Failed to submit withdrawal request');
                }
            }
        } catch (error) {
            console.error('Error creating withdrawal:', error);
            setError(error.message || error.data?.message || 'An unexpected error occurred. Please try again later.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="col-xl-12">
            <Card>
                <Card.Header className="py-3 d-flex justify-content-between bg-transparent border-bottom align-items-center">
                    <h6 className="mb-0 fw-bold">Withdraw Funds</h6>
                </Card.Header>
                <Card.Body>
                    {success && (
                        <Alert variant="success" className="mb-4" dismissible onClose={() => setSuccess(null)}>
                            <strong>Success!</strong> {success}
                            {withdrawResult && (
                                <div className="mt-3">
                                    <table className="table table-sm table-bordered">
                                        <tbody>
                                            <tr>
                                                <td><strong>Withdrawal ID:</strong></td>
                                                <td>{withdrawResult.id}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Reference:</strong></td>
                                                <td>{withdrawResult.reference}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Amount:</strong></td>
                                                <td>${withdrawResult.amount.toLocaleString('en-IN', {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2
                                                })}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Medium:</strong></td>
                                                <td>{withdrawResult.medium}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Bank Account:</strong></td>
                                                <td>{selectedAccount?.accountNumber}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Status:</strong></td>
                                                <td>
                                                    <Badge bg={
                                                        withdrawResult.status === 'pending' ? 'warning' : 
                                                        withdrawResult.status === 'approved' ? 'success' : 
                                                        withdrawResult.status === 'rejected' ? 'danger' : 'secondary'
                                                    }>
                                                        {withdrawResult.status.toUpperCase()}
                                                    </Badge>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </Alert>
                    )}

                    {error && (
                        <Alert variant="danger" className="mb-4" dismissible onClose={() => setError(null)}>
                            <strong>Error!</strong> {error.split('\n').map((line, i) => (
                                <React.Fragment key={i}>
                                    {line}
                                    {i < error.split('\n').length - 1 && <br />}
                                </React.Fragment>
                            ))}
                        </Alert>
                    )}

                    <div className="row g-3">
                        <div className="col-xxl-4 col-xl-4 col-lg-5">
                            <div className="sticky-top" style={{ top: '20px', zIndex: 1 }}>
                                <Card>
                                    <Card.Header>
                                        <Card.Title className="mb-0">Selected Bank Account</Card.Title>
                                    </Card.Header>
                                    <Card.Body>
                                        {selectedAccount ? (
                                            <div className="bank-account-details">
                                                <h5 className="text-primary">{selectedAccount.bankName}</h5>
                                                <div className="mt-3">
                                                    <p className="mb-2 d-flex justify-content-between">
                                                        <span className="text-muted">Account:</span>
                                                        <span className="font-w600">{selectedAccount.accountNumber}</span>
                                                    </p>
                                                    <p className="mb-2 d-flex justify-content-between">
                                                        <span className="text-muted">IFSC:</span>
                                                        <span className="font-w600">{selectedAccount.ifscCode}</span>
                                                    </p>
                                                    <p className="mb-0 d-flex justify-content-between">
                                                        <span className="text-muted">Holder:</span>
                                                        <span className="font-w600">{selectedAccount.accountHolderName}</span>
                                                    </p>
                                                </div>
                                            </div>
                                        ) : (
                                            <Alert variant="warning">
                                                No bank account selected
                                            </Alert>
                                        )}
                                    </Card.Body>
                                </Card>
                            </div>
                        </div>

                        <div className="col-xxl-8 col-xl-8 col-lg-7">
                            {bankAccounts.length === 0 ? (
                                <Alert variant="warning">
                                    No bank accounts available. Please add a bank account first.
                                </Alert>
                            ) : (
                                <Form onSubmit={handleSubmit} className="withdraw-form">
                                    <Form.Group className="mb-4">
                                        <Form.Label>Bank Account <span className="text-danger">*</span></Form.Label>
                                        <Form.Select
                                            name="bankAccountId"
                                            value={formData.bankAccountId}
                                            onChange={handleInputChange}
                                            required
                                            className="form-control-lg"
                                        >
                                            <option value="">Select Bank Account</option>
                                            {bankAccounts.map(account => (
                                                <option key={account._id} value={account._id}>
                                                    {account.bankName} - {account.accountNumber}
                                                    {account.isPrimary ? ' (Primary)' : ''}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>

                                    <Form.Group className="mb-4">
                                        <Form.Label>Amount <span className="text-danger">*</span></Form.Label>
                                        <div className="amount-input-wrapper">
                                            <Form.Control
                                                type="number"
                                                name="amount"
                                                value={formData.amount}
                                                onChange={handleInputChange}
                                                required
                                                className="form-control-lg"
                                                placeholder="Enter amount to withdraw"
                                                min="1"
                                            />
                                        </div>
                                    </Form.Group>

                                    <Form.Group className="mb-4">
                                        <Form.Label>Withdrawal Medium <span className="text-danger">*</span></Form.Label>
                                        <Form.Select
                                            name="medium"
                                            value={formData.medium}
                                            onChange={handleInputChange}
                                            required
                                            className="form-control-lg"
                                        >
                                            {WITHDRAWAL_MEDIUMS.map(medium => (
                                                <option key={medium.value} value={medium.value}>
                                                    {medium.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Form.Text className="text-muted">
                                            Select your preferred withdrawal method
                                        </Form.Text>
                                    </Form.Group>

                                    <div className="text-center mt-4">
                                        <Button 
                                            type="submit" 
                                            variant="primary" 
                                            size="lg"
                                            disabled={submitting || !selectedAccount}
                                        >
                                            {submitting ? 'Processing...' : 'Submit Withdrawal'}
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </div>
                    </div>
                </Card.Body>
            </Card>
        </div>
    );
};

const mapStateToProps = (state) => ({
    auth: state.auth
});

export default connect(mapStateToProps)(WithdrawNew);
